import {
  type ChangeMyEmailDto,
  type ChangeMyUsernameDto,
  type ChangeStaffPasswordDto,
  type CompleteMfaEnrollmentDto,
  type DisableStaffMfaDto,
  type MfaEnrollmentCompletedResponseDto,
  type MfaEnrollmentResponseDto,
  type PrincipalResponseDto,
  type ReauthenticateStaffDto,
  type RecoveryCodesResponseDto,
  type RenameStaffSessionDto,
  type ResetStaffMfaDto,
  type StaffLoginDto,
  type StaffMfaPolicyResponseDto,
  type StaffProfileResponseDto,
  type StaffSessionResponseDto,
  type StaffSessionsPageResponseDto,
  type StaffSessionTokensResponseDto,
  type StartMfaEnrollmentDto,
  type UpdateMyProfileDto,
  type UpdateStaffMfaPolicyDto,
  v2AdminStaffSecurityControllerResetMfa,
  v2AdminStaffSecurityControllerUpdateMfaPolicy,
  v2AuthControllerCompleteMfaEnrollment,
  v2AuthControllerCompleteMfaRotation,
  v2AuthControllerDisableMfa,
  v2AuthControllerLegacyAccess,
  v2AuthControllerLogin,
  v2AuthControllerLogout,
  v2AuthControllerMe,
  v2AuthControllerReauthenticate,
  v2AuthControllerRecoveryCodes,
  v2AuthControllerRefresh,
  v2AuthControllerStartMfaEnrollment,
  v2AuthControllerStartMfaRotation,
  v2StaffSelfServiceControllerChangeEmail,
  v2StaffSelfServiceControllerChangePassword,
  v2StaffSelfServiceControllerChangeUsername,
  v2StaffSelfServiceControllerGetProfile,
  v2StaffSelfServiceControllerListSessions,
  v2StaffSelfServiceControllerRenameSession,
  v2StaffSelfServiceControllerRevokeAllSessions,
  v2StaffSelfServiceControllerRevokeSession,
  v2StaffSelfServiceControllerUpdateProfile
} from '@idc-forms/api-client'
import { V2StaffProfileSchema, V2StaffSessionsPageSchema } from '@idc-forms/schemas/api-v2/auth'
import { assertShape } from '@/shared/api/assert-shape'
import axios from '@/shared/api/axios'
import { API } from '@/shared/constants/api'
import type { typePermission } from '../roles/consts/permissions'
import type { IStatePermission, IUser, IUserAuth } from '../users/models/user'

const fallbackApiV2BaseUrl = import.meta.env.VITE_API_URL_NEST.replace(/\/api\/v1\/?$/, '')
const API_V2_BASE_URL = (import.meta.env.VITE_API_URL_V2 || fallbackApiV2BaseUrl).replace(/\/$/, '')

const requestOptions = {
  baseUrl: API_V2_BASE_URL,
  credentials: 'include' as const,
  throwOnError: true as const
}

type LegacyAuthorizationProfile = {
  id: string
  username: string
  fullname: string
  permissions: typePermission[]
  statePermissions: IStatePermission[]
  typeAccount: IUser['typeAccount']
}

function bearer(accessToken: string) {
  return { Authorization: `Bearer ${accessToken}` }
}

function assertStaffPrincipal(principal: PrincipalResponseDto): void {
  if (principal.kind !== 'staff' || principal.credential !== 'staff_jwt') {
    throw new Error('The authenticated principal is not a staff web session')
  }
}

async function hydrate(session: StaffSessionTokensResponseDto): Promise<IUserAuth> {
  const headers = bearer(session.accessToken)
  const [principalResult, legacyAccessResult] = await Promise.all([
    v2AuthControllerMe({ ...requestOptions, headers }),
    v2AuthControllerLegacyAccess({ ...requestOptions, headers })
  ])

  const principal = principalResult.data
  assertStaffPrincipal(principal)

  const legacyAccessToken = legacyAccessResult.data.accessToken
  const legacyHeaders = bearer(legacyAccessToken)
  const [profileResult, authorizationResult] = await Promise.all([
    axios.get<IUser>(API.auth.me, { headers: legacyHeaders }),
    axios.get<LegacyAuthorizationProfile>(API.auth.renewData, { headers: legacyHeaders })
  ])

  const profile = profileResult.data
  const authorization = authorizationResult.data

  return {
    _id: profile._id,
    id: profile.id,
    publicId: principal.id,
    username: profile.username || authorization.username,
    fullname: profile.fullname || authorization.fullname,
    typeAccount: profile.typeAccount || authorization.typeAccount,
    permissions: principal.permissions as typePermission[],
    statePermissions: authorization.statePermissions,
    accessToken: legacyAccessToken,
    v2AccessToken: session.accessToken,
    v2SessionId: session.sessionId,
    mfa: session.mfa
  }
}

export const staffSessionV2Service = {
  login: async (body: StaffLoginDto): Promise<StaffSessionTokensResponseDto> => {
    const result = await v2AuthControllerLogin({ ...requestOptions, body })
    return result.data
  },

  refresh: async (): Promise<StaffSessionTokensResponseDto> => {
    const result = await v2AuthControllerRefresh(requestOptions)
    return result.data
  },

  hydrate,

  startMfaEnrollment: async (
    accessToken: string,
    body: StartMfaEnrollmentDto
  ): Promise<MfaEnrollmentResponseDto> => {
    const result = await v2AuthControllerStartMfaEnrollment({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  completeMfaEnrollment: async (
    accessToken: string,
    body: CompleteMfaEnrollmentDto
  ): Promise<MfaEnrollmentCompletedResponseDto> => {
    const result = await v2AuthControllerCompleteMfaEnrollment({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  // Cambio de aplicación de autenticación: rota el secreto TOTP sin apagar nunca el 2FA
  // (imprescindible cuando el MFA es obligatorio). El authenticator actual sigue válido
  // hasta que se confirma un código del nuevo.
  startMfaRotation: async (accessToken: string): Promise<MfaEnrollmentResponseDto> => {
    const result = await v2AuthControllerStartMfaRotation({
      ...requestOptions,
      headers: bearer(accessToken)
    })
    return result.data
  },

  completeMfaRotation: async (
    accessToken: string,
    body: CompleteMfaEnrollmentDto
  ): Promise<MfaEnrollmentCompletedResponseDto> => {
    const result = await v2AuthControllerCompleteMfaRotation({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  disableMfa: async (accessToken: string, body: DisableStaffMfaDto): Promise<void> => {
    await v2AuthControllerDisableMfa({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
  },

  regenerateRecoveryCodes: async (accessToken: string): Promise<RecoveryCodesResponseDto> => {
    const result = await v2AuthControllerRecoveryCodes({
      ...requestOptions,
      headers: bearer(accessToken)
    })
    return result.data
  },

  changePassword: async (accessToken: string, body: ChangeStaffPasswordDto): Promise<void> => {
    await v2StaffSelfServiceControllerChangePassword({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
  },

  // Vuelve a probar la identidad SOBRE la sesión vigente para reabrir la ventana de
  // step-up. No acuña sesión ni depende del username: reemplaza al login, que hacía
  // ambas cosas. Devuelve un access token fresco para la sesión actual.
  reauthenticate: async (
    accessToken: string,
    body: ReauthenticateStaffDto
  ): Promise<StaffSessionTokensResponseDto> => {
    const result = await v2AuthControllerReauthenticate({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  getProfile: async (accessToken: string): Promise<StaffProfileResponseDto> => {
    const result = await v2StaffSelfServiceControllerGetProfile({
      ...requestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2StaffProfileSchema, result.data)
  },

  updateProfile: async (
    accessToken: string,
    body: UpdateMyProfileDto
  ): Promise<StaffProfileResponseDto> => {
    const result = await v2StaffSelfServiceControllerUpdateProfile({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  changeUsername: async (
    accessToken: string,
    body: ChangeMyUsernameDto
  ): Promise<StaffProfileResponseDto> => {
    const result = await v2StaffSelfServiceControllerChangeUsername({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  changeEmail: async (
    accessToken: string,
    body: ChangeMyEmailDto
  ): Promise<StaffProfileResponseDto> => {
    const result = await v2StaffSelfServiceControllerChangeEmail({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  listSessions: async (
    accessToken: string,
    after?: string
  ): Promise<StaffSessionsPageResponseDto> => {
    const result = await v2StaffSelfServiceControllerListSessions({
      ...requestOptions,
      headers: bearer(accessToken),
      query: { 'page[size]': '50', ...(after ? { 'page[after]': after } : {}) }
    })
    return assertShape(V2StaffSessionsPageSchema, result.data)
  },

  renameSession: async (
    accessToken: string,
    id: string,
    body: RenameStaffSessionDto
  ): Promise<StaffSessionResponseDto> => {
    const result = await v2StaffSelfServiceControllerRenameSession({
      ...requestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  revokeSession: async (accessToken: string, id: string): Promise<void> => {
    await v2StaffSelfServiceControllerRevokeSession({
      ...requestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  },

  revokeAllSessions: async (accessToken: string): Promise<void> => {
    await v2StaffSelfServiceControllerRevokeAllSessions({
      ...requestOptions,
      headers: bearer(accessToken)
    })
  },

  updateStaffMfaPolicy: async (
    accessToken: string,
    userId: string,
    body: UpdateStaffMfaPolicyDto
  ): Promise<StaffMfaPolicyResponseDto> => {
    const result = await v2AdminStaffSecurityControllerUpdateMfaPolicy({
      ...requestOptions,
      headers: bearer(accessToken),
      path: { id: userId },
      body
    })
    return result.data
  },

  // Rescate admin: limpia el enrolamiento MFA de un usuario que perdió su app y sus
  // códigos de recuperación. Conserva los API tokens salvo `revokeApiTokens` (cuenta
  // comprometida). Requiere un access token del admin con MFA reciente (step-up);
  // acuñar uno con `useStaffStepUp`.
  resetStaffMfa: async (
    accessToken: string,
    userId: string,
    body: ResetStaffMfaDto
  ): Promise<StaffMfaPolicyResponseDto> => {
    const result = await v2AdminStaffSecurityControllerResetMfa({
      ...requestOptions,
      headers: bearer(accessToken),
      path: { id: userId },
      body
    })
    return result.data
  },

  logout: async (accessToken: string): Promise<void> => {
    await v2AuthControllerLogout({
      ...requestOptions,
      headers: bearer(accessToken)
    })
  }
}
